import dayjs, { Dayjs } from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import ArrowDownIcon from '@/assets/icons/arrow-down.svg?react'
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react'
import ArrowRightIcon from '@/assets/icons/arrow-right.svg?react'
import CalendarIcon from '@/assets/icons/calendar.svg?react'
import { Button, Dropdown } from '@/components'
import { cn } from '@/utils/style'

dayjs.extend(isoWeek)

export interface TimeRangeValue {
  from: string
  to: string
}

interface TimeRangeFilterProps {
  initialPreset?: PresetKey | null
  onChange: (range: TimeRangeValue | null) => void
}

export type PresetKey =
  | 'today'
  | 'yesterday'
  | 'last24Hours'
  | 'last7Days'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'

const PRESET_ROWS: PresetKey[][] = [
  ['today', 'yesterday'],
  ['last24Hours', 'last7Days'],
  ['thisWeek', 'lastWeek'],
  ['thisMonth', 'lastMonth'],
]

export function isPresetKey(value: string | null): value is PresetKey {
  return !!value && PRESET_ROWS.flat().includes(value as PresetKey)
}

export function presetRange(key: PresetKey): [Dayjs, Dayjs] {
  const now = dayjs()
  switch (key) {
    case 'today':
      return [now.startOf('day'), now]
    case 'yesterday':
      return [now.subtract(1, 'day').startOf('day'), now.subtract(1, 'day').endOf('day')]
    case 'last24Hours':
      return [now.subtract(24, 'hour'), now]
    case 'last7Days':
      return [now.subtract(7, 'day').startOf('day'), now]
    case 'thisWeek':
      return [now.startOf('isoWeek'), now]
    case 'lastWeek':
      return [now.subtract(1, 'week').startOf('isoWeek'), now.subtract(1, 'week').endOf('isoWeek')]
    case 'thisMonth':
      return [now.startOf('month'), now]
    case 'lastMonth':
      return [now.subtract(1, 'month').startOf('month'), now.subtract(1, 'month').endOf('month')]
  }
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const WEEKDAY_LABELS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

function CalendarPanel({
  selected,
  onSelect,
  align,
}: {
  selected: Dayjs | null
  onSelect: (day: Dayjs) => void
  align: 'left' | 'right'
}) {
  const [viewMonth, setViewMonth] = useState<Dayjs>((selected ?? dayjs()).startOf('month'))

  const currentYear = dayjs().year()
  const yearOptions = useMemo(() => {
    const years: number[] = []
    for (let year = currentYear - 4; year <= currentYear + 1; year++) {
      years.push(year)
    }
    return years
  }, [currentYear])

  // 6 fixed weeks starting from the Monday on/before the 1st
  const days = useMemo(() => {
    const gridStart = viewMonth.startOf('isoWeek')
    return Array.from({ length: 42 }, (_, i) => gridStart.add(i, 'day'))
  }, [viewMonth])

  return (
    <div
      className={cn(
        'absolute top-full mt-2 z-30 w-[340px] bg-panel-white rounded-xl shadow-card-100 p-4',
        align === 'left' ? 'left-0' : 'right-0',
      )}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <button
          type="button"
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-panel-gray-100 text-secondary"
          onClick={() => setViewMonth((prev) => prev.subtract(1, 'month'))}
          aria-label="previous month"
        >
          <ArrowLeftIcon className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2">
          <Dropdown
            rootClassName="t-sm w-[84px]"
            className="h-9"
            options={MONTH_LABELS.map((label, index) => ({
              label,
              value: index,
              className: 't-sm',
            }))}
            value={viewMonth.month()}
            onChange={(value) => setViewMonth((prev) => prev.month(Number(value)))}
          />
          <Dropdown
            rootClassName="t-sm w-[96px]"
            className="h-9"
            options={yearOptions.map((year) => ({
              label: String(year),
              value: year,
              className: 't-sm',
            }))}
            value={viewMonth.year()}
            onChange={(value) => setViewMonth((prev) => prev.year(Number(value)))}
          />
        </div>
        <button
          type="button"
          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-panel-gray-100 text-secondary"
          onClick={() => setViewMonth((prev) => prev.add(1, 'month'))}
          aria-label="next month"
        >
          <ArrowRightIcon className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAY_LABELS.map((label) => (
          <div key={label} className="h-8 flex items-center justify-center t-xs text-tertiary">
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {days.map((day) => {
          const inMonth = day.month() === viewMonth.month()
          const isSelected = !!selected && day.isSame(selected, 'day')
          const isToday = day.isSame(dayjs(), 'day')
          return (
            <button
              key={day.format('YYYY-MM-DD')}
              type="button"
              className={cn(
                'h-9 flex items-center justify-center rounded-lg t-sm cursor-pointer',
                inMonth ? 'text-primary' : 'text-tertiary',
                isSelected
                  ? 'bg-highlight text-white'
                  : cn('hover:bg-panel-gray-100', isToday && 'bg-panel-gray-100'),
              )}
              onClick={() => onSelect(day)}
            >
              {day.date()}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function DateField({
  label,
  value,
  open,
  onToggle,
  onSelect,
  calendarAlign,
}: {
  label: string
  value: Dayjs | null
  open: boolean
  onToggle: () => void
  onSelect: (day: Dayjs) => void
  calendarAlign: 'left' | 'right'
}) {
  return (
    <div className="flex-1 relative">
      <div className="t-sb text-primary mb-2">{label}</div>
      <button
        type="button"
        className="w-full h-11 px-3 flex items-center justify-between border border-neutral-200 rounded-lg t-sm hover:border-neutral-300"
        onClick={onToggle}
      >
        <span className={value ? 'text-primary' : 'text-tertiary'}>
          {value ? value.format('MMM DD, YYYY') : 'MMM DD, YYYY'}
        </span>
        <CalendarIcon className="w-4 h-4 text-tertiary" />
      </button>
      {open && <CalendarPanel selected={value} onSelect={onSelect} align={calendarAlign} />}
    </div>
  )
}

export function TimeRangeFilter({ initialPreset, onChange }: TimeRangeFilterProps) {
  const { t } = useTranslation('modelRoute')
  const containerRef = useRef<HTMLDivElement>(null)

  const presetLabel = (key: PresetKey) =>
    t(
      `billingDetail.timeRange.${key}`,
      {
        today: 'Today',
        yesterday: 'Yesterday',
        last24Hours: 'Last 24 Hours',
        last7Days: 'Last 7 Days',
        thisWeek: 'This Week',
        lastWeek: 'Last Week',
        thisMonth: 'This Month',
        lastMonth: 'Last Month',
      }[key],
    )

  const [open, setOpen] = useState(false)
  const [label, setLabel] = useState<string | null>(() =>
    initialPreset ? presetLabel(initialPreset) : null,
  )
  const [preset, setPreset] = useState<PresetKey | null>(initialPreset ?? null)
  const [start, setStart] = useState<Dayjs | null>(() =>
    initialPreset ? presetRange(initialPreset)[0] : null,
  )
  const [end, setEnd] = useState<Dayjs | null>(() =>
    initialPreset ? presetRange(initialPreset)[1] : null,
  )
  const [calendarFor, setCalendarFor] = useState<'start' | 'end' | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
        setCalendarFor(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const selectPreset = (key: PresetKey) => {
    const [from, to] = presetRange(key)
    setPreset(key)
    setStart(from)
    setEnd(to)
    setCalendarFor(null)
  }

  const applyDisabled =
    (start === null) !== (end === null) || (!!start && !!end && start.isAfter(end))

  const apply = () => {
    if (start && end) {
      onChange({ from: start.toISOString(), to: end.toISOString() })
      setLabel(
        preset
          ? presetLabel(preset)
          : `${start.format('MMM DD, YYYY')} - ${end.format('MMM DD, YYYY')}`,
      )
    } else {
      onChange(null)
      setLabel(null)
    }
    setOpen(false)
    setCalendarFor(null)
  }

  const reset = () => {
    setPreset(null)
    setStart(null)
    setEnd(null)
    setLabel(null)
    setCalendarFor(null)
    onChange(null)
    setOpen(false)
  }

  return (
    <div className="flex flex-col gap-2" ref={containerRef}>
      <div className="text-secondary t-sb">{t('billingDetail.timeRange.label', 'Time Range')}</div>
      <div className="relative">
        <button
          type="button"
          className="w-60 h-11 px-4 flex items-center justify-between border border-neutral-200 rounded-lg t-sb text-primary bg-panel-white"
          onClick={() => setOpen((prev) => !prev)}
        >
          <span className="truncate">{label ?? t('billingDetail.timeRange.all', 'All')}</span>
          <ArrowDownIcon
            className={cn('w-4 h-4 text-tertiary transition-transform', open && 'rotate-180')}
          />
        </button>

        {open && (
          <div className="absolute right-0 top-full mt-2 z-20 w-[470px] bg-panel-white rounded-xl shadow-card-100 p-4">
            <div className="flex flex-col gap-3">
              {PRESET_ROWS.map((row) => (
                <div key={row[0]} className="grid grid-cols-2 gap-3">
                  {row.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={cn(
                        'h-10 rounded-lg border t-sm text-primary hover:bg-panel-gray-100',
                        preset === key ? 'border-highlight text-highlight' : 'border-neutral-200',
                      )}
                      onClick={() => selectPreset(key)}
                    >
                      {presetLabel(key)}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            <div className="flex gap-4 mt-4">
              <DateField
                label={t('billingDetail.timeRange.startDate', 'Start Date')}
                value={start}
                open={calendarFor === 'start'}
                calendarAlign="left"
                onToggle={() => setCalendarFor((prev) => (prev === 'start' ? null : 'start'))}
                onSelect={(day) => {
                  setStart(day.startOf('day'))
                  setPreset(null)
                  setCalendarFor(null)
                }}
              />
              <DateField
                label={t('billingDetail.timeRange.endDate', 'End Date')}
                value={end}
                open={calendarFor === 'end'}
                calendarAlign="right"
                onToggle={() => setCalendarFor((prev) => (prev === 'end' ? null : 'end'))}
                onSelect={(day) => {
                  setEnd(day.endOf('day'))
                  setPreset(null)
                  setCalendarFor(null)
                }}
              />
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <Button type="outline" onClick={reset}>
                {t('billingDetail.timeRange.reset', 'Reset')}
              </Button>
              <Button type="secondary" disabled={applyDisabled} onClick={apply}>
                {t('billingDetail.timeRange.apply', 'Apply')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
